import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || "https://api.materiallogs.com";

// Create a new axios instance for the authentication API
const authAPI = axios.create({
    baseURL: `${API_BASE_URL}/users`, // Assuming your login endpoint is under `/users`
    withCredentials: true, // Include cookies if required
    headers: {
        "Content-Type": "application/json",
    },
});

authAPI.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            setAuthToken(""); // Clear token
            // window.location.href = "/"; // Redirect to login
        }
        return Promise.reject(error);
    },
);


/**
 * Set the Authorization token in the header for future requests
 * @param token string - The JWT token returned after login
 */
const setAuthToken = (token: string) => {
    if (token) {
        authAPI.defaults.headers["Authorization"] = `Bearer ${token}`;
    } else {
        delete authAPI.defaults.headers["Authorization"];
    }
};

/**
 * Perform user login
 */
export const login = async (email: string, password: string) => {
    try {
        const response = await authAPI.post("/login", { email, password });
        if (response.data.user) {
            localStorage.setItem("token", response.data.user.userToken);
            setAuthToken(response.data.user.userToken);
        } else {
            console.error("No token found in login response:", response.data);
        }
        return response.data;
    } catch (error: any) {
        console.error("Login error:", error.response || error.message);
        throw error.response?.data || new Error("Login failed.");
    }
};

/**
 * Perform user registration
 */
export const register = async (username: string, email: string, password: string) => {
    try {
        const response = await authAPI.post("/register", { username, email, password });
        return response.data;
    } catch (error: any) {
        console.error("Registration error:", error.response || error.message);
        throw error.response?.data || new Error("Registration failed.");
    }
};

/**
 * Request password reset
 */
export const forgotPassword = async (email: string) => {
    try {
        const response = await authAPI.post("/forgot-password", { email });
        return response.data;
    } catch (error: any) {
        console.error("Forgot Password error:", error.response || error.message);
        throw error.response?.data || new Error("Failed to send password reset link.");
    }
};

/**
 * Reset password
 */
export const resetPassword = async (code: string, userId: string, password: string) => {
    try {
        const response = await authAPI.post("/reset-password", { code, userId, password });
        return response.data;
    } catch (error: any) {
        console.error("Reset Password error:", error.response || error.message);
        throw error.response?.data || new Error("Failed to reset password.");
    }
};

/**
 * Fetch user profile
 */
export const fetchUserProfile = async (token: string) => {
    try {
        const response = await authAPI.get("/profile", {
            headers: { Authorization: `Bearer ${token}` },
        });
        return response.data;
    } catch (error: any) {
        console.error("Error fetching user profile:", error.response || error.message);
        throw error.response?.data || new Error("Failed to fetch user profile.");
    }
};

/**
 * Perform user logout
 */
export const logout = async () => {
    try {
        const response = await authAPI.post("/logout");
        localStorage.removeItem("token");
        setAuthToken("");
        console.log("Logged out successfully.");
        return response.data;
    } catch (error: any) {
        console.error("Logout error:", error.response || error.message);
        localStorage.removeItem("token");
        setAuthToken("");
        throw error.response?.data || new Error("Failed to logout.");
    }
};

/**
 * Verify the token
 */
export const verifyToken = async () => {
    try {
        const response = await authAPI.get("/verify");
        return response.data;
    } catch (error: any) {
        console.error("Error verifying token:", error.response || error.message);
        throw error.response?.data || new Error("Failed to verify token.");
    }
};

/**
 * Verify email
 */
export const verifyEmail = async (code: string, userId: string) => {
    try {
        const response = await authAPI.get(`/verify-code?code=${code}&userId=${userId}`);
        if (response.data.user) {
            localStorage.setItem("token", response.data.user.userToken);
            setAuthToken(response.data.user.userToken);
        } else {
            console.error("No token found in email verification response:", response.data);
        }
        return response.data;
    } catch (error: any) {
        console.error("Email verification error:", error.response || error.message);
        throw error.response?.data || new Error("Email verification failed.");
    }
};

// Load the token from localStorage if available and set it in axios headers
const token = localStorage.getItem("token");
if (token) {
    setAuthToken(token);
}

export default authAPI;
